import {
  Allergen,
  DietType,
  ExtraProductsQuery,
  MealProductsQuery,
  MedusaLineItem,
  MedusaProductVariant,
  MeQuery,
  MyBoxSettingsQuery,
  MyOrdersQuery,
  SubscriptionFrequency,
  StripePaymentMethod,
  MyPaymentMethodsQuery,
  OrdersQuery,
  SubscriptionOrder
} from 'lib/home/generated/types/api';

/**
 * Filters the available meal products based on the user's subscription plan and meal preferences.
 *
 * @param mealProducts MealProductsQuery The collection of available meals within the Medusa storefront.
 * @param meData MyBoxSettingsQuery The user's subscription and meal preference data.
 * @param filters Function An optional function to filter the meal products.
 * @returns MedusaProductVariant[] The filtered selection of meal products.
 */
export const selectMealProductsByPlanSize = (
  mealProducts: MealProductsQuery,
  meData: MyBoxSettingsQuery,
  filters?: (variant: MedusaProductVariant) => boolean
): MedusaProductVariant[] => {
  const {
    meal_preferences,
    subscription: {
      product_variant: { metadata }
    }
  } = meData.me;

  const { meal_size } = metadata;
  const flatMealProducts = Object.values(mealProducts).flat() as MedusaProductVariant[];

  if (!meal_size || !meal_preferences) return;

  return flatMealProducts.filter((variant: MedusaProductVariant) => {
    if (!variant.options.find(({ value }) => value.toLowerCase() === meal_size.toLowerCase())) return false;
    if (!variant.dietary_information) return false;
    if (!filters(variant)) return false;

    return true;
  });
};

export const selectCardInfoFromMedusaOrder = (order: MyOrdersQuery['customerOrders'][number]) => {
  const { last4 = null, brand = null } = order?.payments[0]?.data?.charges?.data[0]?.payment_method_details?.card || {};
  return { last4, brand };
};

/**
 * Return the meal product in the cart line item from the collection of meal products.
 *
 * @param cartItem MedusaLineItem The cart item.
 * @param mealProducts MealProductsQuery The collection of meal products from Medusa storefront.
 * @returns
 */
export const selectCartItemMealFromMealProducts = (
  cartItem: MedusaLineItem,
  mealProducts: MealProductsQuery
): MedusaProductVariant => {
  const flatMealProducts = Object.values(mealProducts).flat() as MedusaProductVariant[];

  return flatMealProducts.find(product => product.id === cartItem.variant.id);
};

export const selectCurrentOrderFromOrders = (orders: OrdersQuery): SubscriptionOrder =>
  orders.me.subscription.current_order;

export const selectDefaultAddressFromMe = (me: MeQuery['me']) =>
  me.shipping_addresses.find(address => address.is_default);

export const selectDefaultCardInfoFromPaymentMethods = (paymentMethods: MyPaymentMethodsQuery) => {
  const defaultPaymentMethod = paymentMethods.me.payment_methods.find(method => method.is_default);
  const { brand, last4 } = defaultPaymentMethod.card;

  return { brand, last4 };
};

export const selectDefaultFromPaymentMethods = (paymentMethods: MyPaymentMethodsQuery): StripePaymentMethod =>
  paymentMethods.me.payment_methods.find(pm => pm.is_default);

/**
 * Filters the available meal products based on the user's subscription plan and meal preferences.
 *
 * @param mealProducts MealProductsQuery The collection of available meals within the Medusa storefront.
 * @param meData MyBoxSettingsQuery The user's subscription and meal preference data.
 * @returns MedusaProductVariant[] The filtered selection of meal products.
 */
export const selectAlternativeMealProductsByPlanSize = (
  cartItem: MedusaLineItem,
  mealProducts: MealProductsQuery,
  meData: MyBoxSettingsQuery
): MedusaProductVariant[] =>
  selectMealProductsByPlanSize(mealProducts, meData, variant => {
    if (variant.id === cartItem?.variant_id) return false;
    return true;
  });

/**
 * Filters the available extra products based on the user's subscription plan and meal preferences.
 *
 * @param extraProducts ExtraProductsQuery The collection of available meals within the Medusa storefront.
 * @param me MyBoxSettingsQuery The user's subscription and meal preference data.
 * @returns MedusaProductVariant[] The filtered selection of meal products.
 */
export const selectExtraProductsByPlanSize = (
  extraProducts: ExtraProductsQuery,
  me: MeQuery['me']
): MedusaProductVariant[] => {
  const {
    meal_preferences,
    subscription: {
      product_variant: { metadata }
    }
  } = me;

  const { meal_size } = metadata;
  const flatMealProducts = Object.values(extraProducts).flat() as MedusaProductVariant[];

  if (!meal_size || !meal_preferences) return;

  return flatMealProducts
    .filter((variant: MedusaProductVariant) => {
      if (!variant.options.find(({ value }) => value.toLowerCase() === meal_size.toLowerCase())) return false;
      if (!variant.dietary_information) return false;

      return true;
    })
    .sort((a, b) => (a.product.title < b.product.title ? -1 : 1));
};

/**
 * Returns the user's subscription frequency from MeQuery data
 *
 * @param me MeQuery["me"] The user's data returned from MeQuery
 * @returns SubscriptionFrequency The user's subscription frequency
 */
export const selectFrequencyFromMe = (me: MeQuery['me']): SubscriptionFrequency => {
  const {
    subscription: { frequency }
  } = me;

  return frequency;
};

/**
 * Returns the user's diet and allergen meal preferences from MeQuery data
 *
 * @param me MeQuery["me"] The user's data returned from MeQuery
 * @returns {DietType[], Allergen[]} The collection of diet and allergen preferences
 */
export const selectMealPreferencesFromMe = (me: MeQuery['me']): { diets: DietType[]; allergens: Allergen[] } => {
  const { diets, allergens } = me.meal_preferences;
  return { diets, allergens };
};
